import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { type RichiestaModuloDTOStatoValidazione } from '@framework/api-client/api/inquiries-compile/schemas/richiestaModuloDTOStatoValidazione.ts';
import { type IEngineValidationContext } from '@framework/common-web/engine/engine-validation/IEngineValidationContext.ts';
import { type SubmissionModuleState } from '@framework/common-web/frontoffice/module/fsm/SubmissionModuleState.ts';
import { type IFrontOfficeModule } from '@framework/common-web/frontoffice/module/IFrontOfficeModule.ts';
import { type IEngineSection } from '@framework/engine/schema/sections/IEngineSection.ts';
import { useFormAugmentedContext } from '@framework/form/form/augmented/FormAugmentedContext.tsx';
import { ManagedSubmitButton } from '@framework/form/form/submit/ManagedSubmitButton.tsx';
import { ButtonResponsive } from '@framework/ui/components/button/ButtonResponsive.tsx';
import { FlexRow } from '@framework/ui/components/row/FlexRow.tsx';
import { Divider, Space } from 'antd';
import type React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { type AnyObject, type Maybe, type ObjectSchema } from 'yup';
import { DownloadXmlValuesButton } from '#features/packages/xsd-web/src/frontoffice/download-xml-values/DownloadXmlValuesButton.tsx';
import { SubmissionCancelButton } from '../../navigation/buttons/SubmissionCancelButton.tsx';
import { CardFooterButtons } from '../../navigation/CardFooterButtons.tsx';
import { type ISubmissionNavigator } from '../../navigation/useSubmissionNavigation.ts';
import { DownloadOmissisSubmissionButton } from '../../submission/download-pdf/DownloadOmissisSubmissionButton.tsx';
import { DownloadSubmissionModuleButton } from '../../submission/download-pdf/DownloadSubmissionModuleButton.tsx';
import { SubmissionLogic } from '../../submission/SubmissionLogic.ts';
import { useDraftSaveModuleForm } from '../hooks/useDraftSaveModuleForm.ts';
import { useCheckSchemaHasActiveOmissis } from './omissis/useCheckSchemaHasActiveOmissis.ts';
import { DraftSubmitFormButton } from './submit/DraftSubmitFormButton.tsx';
import { SubmissionTitle } from './title/SubmissionTitle.tsx';
import {
  type IOnAfterSave,
  type IOnBeforeSave,
} from './useSubmitModuleForm.ts';

const SubmissionContent = styled.div`
  padding: 12px 0;
  padding-top: 24px;
`;

const BeforeSubmissionContent = styled.div``;

export interface IModuleFormWrapperProps {
  children?: React.ReactNode;
  currentTitle?: React.ReactNode;
  submissionModuleId: Maybe<string>;
  submissionId: Maybe<string>;
  submission: RichiestaDTO | null | undefined;
  validationSchema: ObjectSchema<AnyObject>;
  handleNavigation: ISubmissionNavigator;
  validationState: RichiestaModuloDTOStatoValidazione | null | undefined;
  moduleState: SubmissionModuleState;
  hasSignatures: boolean;

  isModuleSigned?: boolean;

  isReadonly?: boolean;

  /** Funzione chiamata al salvataggio avvenuto con successo in bozza */
  onDraftSuccess?: () => Promise<void>;

  /** Spazio prima del module */
  beforeModule?: React.ReactNode;

  /** Indica che la richiesta può essere completata in automatico senza bisogno del completamento manuale dell'utente */
  canAutoComplete?: boolean;

  /** Funzioni che vengono chiamate prima e dopo il salvataggio del modulo (pulsante "Salva") */
  onBeforeSave?: IOnBeforeSave;
  onAfterSave?: IOnAfterSave;

  /** Campi necessari per la costruzione del context snapshot */
  validationContext: IEngineValidationContext;
  sections: IEngineSection[];
  module: IFrontOfficeModule | null | undefined;
}

/**
 * Componente padre che incornicia il form di un modulo con tutte le sue sezioni.
 * Aggiunge i pulsanti di submit e di salvataggio in bozza.
 */
export function ModuleFormWrapper(props: IModuleFormWrapperProps) {
  const {
    children,
    currentTitle,
    submissionModuleId,
    submissionId,
    submission,
    handleNavigation,
    validationState,
    isReadonly,
    validationSchema,
    moduleState,
    hasSignatures,
    onDraftSuccess,
    beforeModule,
    canAutoComplete,
    onBeforeSave,
    onAfterSave,
    isModuleSigned,
    validationContext,
    sections,
    module,
  } = props;

  const hideProgressBar =
    SubmissionLogic.isSubmissionSent(submission) || canAutoComplete;

  const { t } = useTranslation();

  const { initialValues } = useFormAugmentedContext();
  const hasOmissis = useCheckSchemaHasActiveOmissis(
    validationSchema,
    initialValues,
    validationContext,
  );

  const onDraftValidSubmit = useDraftSaveModuleForm({
    submissionModuleId,
    submissionId,
    validationSchema,
    validationContext,
    sections,
    onSuccess: onDraftSuccess,
    onBeforeSave,
    onAfterSave,
  });

  const canNext = useMemo(
    () => handleNavigation.canNext(submissionModuleId),
    [handleNavigation, submissionModuleId],
  );

  const canPrevious = useMemo(
    () => handleNavigation.canPrevious(submissionModuleId),
    [handleNavigation, submissionModuleId],
  );

  return (
    <>
      <SubmissionTitle
        currentTitle={currentTitle}
        moduleState={moduleState}
        validationState={validationState}
        hasSignatures={hasSignatures}
        hideProgressBar={hideProgressBar}
      />
      {beforeModule ? (
        <BeforeSubmissionContent>{beforeModule}</BeforeSubmissionContent>
      ) : (
        <Divider style={{ margin: 0 }} />
      )}
      <SubmissionContent>{children}</SubmissionContent>
      <CardFooterButtons>
        <FlexRow
          right={
            <Space>
              {module?.agidFormId && (
                <DownloadXmlValuesButton module={module} />
              )}
              {!isReadonly && <SubmissionCancelButton />}
              <DownloadSubmissionModuleButton
                submissionId={submissionId}
                submissionModuleId={submissionModuleId}
                isModuleSigned={isModuleSigned}
                disabled={
                  SubmissionLogic.isSubmissionNew(submission) ||
                  !validationState?.visitato
                }
              />
              {submissionId && hasOmissis && (
                <DownloadOmissisSubmissionButton
                  submissionId={submissionId}
                  submissionModuleId={submissionModuleId}
                  disabled={
                    SubmissionLogic.isSubmissionNew(submission) ||
                    !validationState?.visitato
                  }
                />
              )}
              {!isReadonly && (
                <DraftSubmitFormButton
                  onValid={onDraftValidSubmit}
                  validationState={validationState}
                />
              )}
              {canNext &&
                (isReadonly ? (
                  <ButtonResponsive
                    size="large"
                    type="primary"
                    text={t('form.avanti', 'Avanti')}
                    iconRightComponent={ArrowRightOutlined}
                    iconRightAriaLabel={t('form.avanti', 'Avanti')}
                    onClick={() => {
                      if (!submissionModuleId) return;
                      handleNavigation.navigate(submissionModuleId, 'next');
                    }}
                  />
                ) : (
                  <ManagedSubmitButton
                    size="large"
                    type="primary"
                    text={t('form.avanti', 'Avanti')}
                    iconRightComponent={ArrowRightOutlined}
                    iconRightAriaLabel={t('form.avanti', 'Avanti')}
                    // iconRight={<ArrowRightOutlined />}
                    onValid={() => {
                      if (!submissionModuleId) return;
                      setTimeout(() => {
                        handleNavigation.navigate(submissionModuleId, 'next');
                      }, 10);
                    }}
                  />
                ))}
            </Space>
          }
          left={
            canPrevious ? (
              <ButtonResponsive
                size="large"
                type="default"
                text={t('form.indietro', 'Indietro')}
                iconLeftComponent={ArrowLeftOutlined}
                iconLeftAriaLabel={t('form.indietro', 'Indietro')}
                onClick={() => {
                  if (!submissionModuleId) return;
                  setTimeout(() => {
                    handleNavigation.navigate(submissionModuleId, 'previous');
                  }, 10);
                }}
              />
            ) : undefined
          }
        />
      </CardFooterButtons>
    </>
  );
}
